import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BackHomeButton from '../components/BackHomeButton';
import './EmployerRegistration.css';

function EmployerRegistration() {
  // SVG Eye Icon
  const EyeIcon = ({ visible }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#888"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" fill={visible ? '#888' : 'none'} />
    </svg>
  );
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    // Organization Details
    companyName: '',
    numberOfEmployees: '',
    sector: '',
    website: '',
    brandName: '',
    gstNumber: '',
    description: '',
    hasTransgenderPolicy: '',
    openToAccommodation: '',
    
    // Contact Information
    district: '',
    mandal: '',
    city: '',
    pincode: '',
    contactPersonName: '',
    department: '',
    designation: '',
    mobileNumber: '',
    alternativeMobile: '',
    emailId: '',
    
    // Documents
    registrationCertificate: null,
    annualReport: null,
    transgenderPolicyCertificate: null,
    companyLogo: null,
    
    // Login Details
    loginEmail: '',
    password: '',
    confirmPassword: '',
    otp: '',
    otpSent: false
  });

  // Form state management
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isOtpSending, setIsOtpSending] = useState(false);

  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Terms and Conditions modal state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // District options (same as candidate registration)
  const districts = [
    'Adilabad', 'Bhadradri Kothagudem', 'Jagtial', 'Jangaon', 'Jayashankar Bhupalpally',
    'Jogulamba Gadwal', 'Kamareddy', 'Karimnagar', 'Kumuram Bheem', 'Mahabubabad',
    'Mahabubnagar', 'Mancherial', 'Medak', 'Medchal–Malkajgiri', 'Mulugu',
    'Nagarkurnool', 'Nalgonda', 'Narayanpet', 'Nirmal', 'Nizamabad',
    'Peddapalli', 'Rajanna Sircilla', 'Rangareddy', 'Sangareddy', 'Siddipet',
    'Suryapet', 'Vikarabad', 'Wanaparthy', 'Warangal Rural', 'Warangal Urban',
    'Yadadri Bhuvanagiri', 'Hyderabad', 'Secunderabad', 'Khammam'
  ];

  // Job sectors (same as candidate registration)
  const jobSectors = [
    'IT/Computer - Hardware & Networking',
    'IT/Computer - Software',
    'ITES/BPO',
    'KPO/Research/Analytics',
    'Telecom',
    'Internet Service Provider (ISP)',
    'Hotels/Hospitality/Restaurant',
    'Airlines/Aviation/Aerospace',
    'Travel/Tourism'
  ];

  // Departments
  const departments = [
    'General Management',
    'Marketing',
    'Operations',
    'Accounting and Finance',
    'Sales',
    'Human Resource',
    'Technology',
    'Purchase'
  ];

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0] || null
      }));
    } else {
      // Input cleaning per-field
      if (name === 'mobileNumber' || name === 'alternativeMobile') {
        const formatted = formatIndianMobileInput(value);
        setFormData(prev => ({ ...prev, [name]: formatted }));
      } else if (name === 'pincode') {
        const onlyDigits = value.replace(/[^0-9]/g, '').slice(0, 6);
        setFormData(prev => ({ ...prev, pincode: onlyDigits }));
      } else if (name === 'companyName') {
        const onlyText = value.replace(/[^A-Za-z\s]/g, '');
        setFormData(prev => ({ ...prev, companyName: onlyText }));
      } else if (name === 'mandal' || name === 'city' || name === 'contactPersonName' || name === 'designation') {
        const onlyText = value.replace(/[^A-Za-z\s]/g, '');
        setFormData(prev => ({ ...prev, [name]: onlyText }));
      } else if (name === 'gstNumber') {
        const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
        setFormData(prev => ({ ...prev, gstNumber: cleaned }));
      } else if (name === 'emailId' || name === 'loginEmail') {
        const cleanedEmail = value.replace(/\s+/g, '');
        setFormData(prev => ({ ...prev, [name]: cleanedEmail }));
      } else {
        // Ensure we never set undefined/null for controlled inputs
        setFormData(prev => ({
          ...prev,
          [name]: typeof value === 'string' ? value : ''
        }));
      }
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Format Indian mobile to "+91 ##########" while typing; allow only digits in number part
  const formatIndianMobileInput = (raw) => {
    const digits = raw.replace(/[^0-9]/g, '');
    let rest = digits;
    if (rest.startsWith('91')) {
      rest = rest.slice(2);
    }
    // Enforce starting digit to be 6/7/8/9 by dropping invalid leading digits
    rest = rest.replace(/^[0-5]+/, '');
    // Limit to 10 digits max
    rest = rest.slice(0, 10);
    return rest.length > 0 ? `+91 ${rest}` : '+91 ';
  };

  // File validation
  const validateFile = (file, maxSize = 10 * 1024 * 1024) => {
  if (!file) return '';
    
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      return 'Only .jpg, .jpeg, .png, .pdf, .doc, .docx file types are allowed';
    }
    
    if (file.size > maxSize) {
      return 'File size should be below 10 MB';
    }
    
    return '';
  };

  // Step validation
  const validateStep = (step) => {
    // Only validate fields for the current step
    const newErrors = {};
    let missingFields = [];
    switch (step) {
      case 1:
        if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
        else if (!/^[A-Za-z\s]+$/.test(formData.companyName)) newErrors.companyName = 'Use only letters and spaces';
        if (!formData.numberOfEmployees) newErrors.numberOfEmployees = 'Number of employees is required';
        if (!formData.sector) newErrors.sector = 'Sector is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';
        if (formData.hasTransgenderPolicy === '') newErrors.hasTransgenderPolicy = 'Please select whether you have Transgender Persons policy';
        if (formData.openToAccommodation === '') newErrors.openToAccommodation = 'Please select whether you are open to reasonable accommodation';
        if (formData.gstNumber) {
          const gstOk = /^[A-Z0-9]{15}$/.test(formData.gstNumber);
          if (!gstOk) newErrors.gstNumber = 'GST number must be 15 characters (A-Z, 0-9)';
        }
        break;
      case 2:
        if (!formData.district) newErrors.district = 'District is required';
        if (!formData.mandal.trim()) newErrors.mandal = 'Mandal is required';
        else if (!/^[A-Za-z\s]+$/.test(formData.mandal)) newErrors.mandal = 'Use only letters and spaces';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        else if (!/^[A-Za-z\s]+$/.test(formData.city)) newErrors.city = 'Use only letters and spaces';
        if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
        else if (!/^[1-9][0-9]{5}$/.test(formData.pincode)) newErrors.pincode = 'Please enter a valid 6-digit pincode';
        if (!formData.contactPersonName.trim()) newErrors.contactPersonName = 'Contact person name is required';
        else if (!/^[A-Za-z\s]{1,50}$/.test(formData.contactPersonName)) newErrors.contactPersonName = 'Use only letters and spaces (max 50)';
        if (!formData.department.trim()) newErrors.department = 'Department is required';
        if (!formData.designation.trim()) newErrors.designation = 'Designation is required';
        else if (!/^[A-Za-z\s]{1,50}$/.test(formData.designation)) newErrors.designation = 'Use only letters and spaces (max 50)';
        if (!formData.mobileNumber.trim()) newErrors.mobileNumber = 'Mobile number is required';
        else {
          const validMobile = /^\+91\s?[6-9]\d{9}$/.test(formData.mobileNumber);
          if (!validMobile) newErrors.mobileNumber = 'Enter valid Indian mobile: +91 10-digit starting 6-9';
        }
        if (!formData.emailId.trim()) newErrors.emailId = 'Email is required';
        else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.emailId)) newErrors.emailId = 'Please enter a valid email address';
        break;
      case 3:
        // Registration Certificate - Mandatory
        if (!formData.registrationCertificate) {
          newErrors.registrationCertificate = 'Registration certificate is required';
        } else {
          const regCertError = validateFile(formData.registrationCertificate);
          if (regCertError) newErrors.registrationCertificate = regCertError;
        }
        
        // Annual Report - Mandatory
        if (!formData.annualReport) {
          newErrors.annualReport = 'Annual report is required';
        } else {
          const annualReportError = validateFile(formData.annualReport);
          if (annualReportError) newErrors.annualReport = annualReportError;
        }
        
        // Transgender Policy Certificate - OPTIONAL (no validation)
        
        // Company Logo - Mandatory
        if (!formData.companyLogo) {
          newErrors.companyLogo = 'Company logo is required';
        } else {
          const logoError = validateFile(formData.companyLogo);
          if (logoError) newErrors.companyLogo = logoError;
        }
        break;
      case 4:
        if (!formData.loginEmail.trim()) newErrors.loginEmail = 'Login Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.loginEmail)) newErrors.loginEmail = 'Please enter a valid email address';
        if (!formData.password) newErrors.password = 'You missed this';
        else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters long';
        else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) newErrors.password = 'Password must contain uppercase, lowercase and number';
        if (!formData.confirmPassword) newErrors.confirmPassword = 'You missed this';
        else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        break;
      default:
        break;
    }
    // For step 4, only set errors if submitAttempted is true
    if (step === 4) {
      if (submitAttempted) {
        setErrors(newErrors);
      }
    } else {
      setErrors(newErrors);
    }
    // Do not show alert here; just return validation result
    return Object.keys(newErrors).length === 0;
  };

  // Navigation functions
  const nextStep = (e) => {
    // Prevent any form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Only validate current step, not the next step
    if (validateStep(currentStep)) {
      // Only clear errors for current step, not all
      if (currentStep < 4) setErrors({});
      // Reset submitAttempted when moving to step 4
      if (currentStep === 3) setSubmitAttempted(false);
      setCurrentStep(prev => {
        // If moving from step 3 to 4, preserve file inputs
        if (prev === 3) {
          // Do nothing, file inputs are already in formData
        }
        return Math.min(prev + 1, 4);
      });
    } else {
      // Show alert if validation fails
      alert('Please fill in all required fields correctly before proceeding.');
    }
  };

  const prevStep = (e) => {
    // Prevent any form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Only clear errors for current step, not all
    if (currentStep > 1) setErrors({});
    setCurrentStep(prev => {
      // If moving from step 4 to 3, preserve file inputs
      if (prev === 4) {
        // Do nothing, file inputs are already in formData
      }
      return Math.max(prev - 1, 1);
    });
  };

  // Send OTP
  const sendOTP = async () => {
    if (!formData.loginEmail.trim()) {
      setErrors(prev => ({ ...prev, loginEmail: 'Email is required to send OTP' }));
      return;
    }

    setIsOtpSending(true);
    
    try {
      // Call backend API to send OTP via email
      const response = await fetch('https://tstranscareers.in/api/api/send-otp-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.loginEmail,
          recipientName: formData.contactPersonName || 'Employer'
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setFormData(prev => ({ ...prev, otpSent: true }));
        alert(`OTP has been sent to ${formData.loginEmail}. Please check your email.`);
      } else {
        alert(result.message || 'Failed to send OTP. Please try again.');
      }
      
    } catch (error) {
      console.error('Send OTP error:', error);
      alert('Failed to send OTP. Please check your connection and try again.');
    } finally {
      setIsOtpSending(false);
    }
  };

  // Handle Terms Acceptance
  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
    // Trigger form submission after accepting terms
    setTimeout(() => {
      document.querySelector('.registration-form').dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      );
    }, 100);
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    // Check if terms have been accepted
    if (!termsAccepted) {
      setShowTermsModal(true);
      return;
    }

    // Only validate step 4 fields on submit
    if (!validateStep(4)) return;

    if (!formData.otp.trim()) {
      setErrors(prev => ({ ...prev, otp: 'Please enter OTP' }));
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('🔐 Verifying OTP...');
      
      // First verify OTP via backend
      const otpVerifyResponse = await fetch('https://tstranscareers.in/api/api/verify-otp-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.loginEmail,
          otp: formData.otp
        })
      });

      console.log('OTP Verify Response Status:', otpVerifyResponse.status);
      
      const otpVerifyResult = await otpVerifyResponse.json();
      console.log('OTP Verify Result:', otpVerifyResult);

      if (!otpVerifyResponse.ok || !otpVerifyResult.success) {
        console.error('❌ OTP verification failed:', otpVerifyResult.message);
        setErrors(prev => ({ ...prev, otp: otpVerifyResult.message || 'Invalid OTP' }));
        alert(otpVerifyResult.message || 'Invalid OTP. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // OTP verified successfully, now proceed with registration
      console.log('✅ OTP verified successfully, proceeding with registration...');

      // Create FormData for file upload
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== 'otp' && key !== 'otpSent' && key !== 'confirmPassword') {
          if (formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          } else {
            if (key === 'mobileNumber' || key === 'alternativeMobile') {
              const digits = formData[key].replace(/\D/g, '').slice(-10);
              formDataToSend.append(key, digits);
            } else {
              formDataToSend.append(key, formData[key]);
            }
          }
        }
      });

      console.log('📤 Sending registration data to backend...');

      // Send data to backend
      const response = await fetch('https://tstranscareers.in/api/register-organization', {
        method: 'POST',
        body: formDataToSend
      });

      console.log('Registration Response Status:', response.status);
      console.log('Registration Response Headers:', response.headers.get('content-type'));
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('❌ Non-JSON response received:', textResponse.substring(0, 200));
        throw new Error('Server returned an invalid response. Please check if the backend is running correctly.');
      }
      
      const data = await response.json();
      console.log('Registration Response Data:', data);

      if (response.ok) {
        console.log('✅ Registration successful!');
        alert('Registration successful! Please login with your email and password.');
        navigate('/login');
      } else {
        console.error('❌ Registration failed:', data.message);
        alert(data.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('❌ Registration error (full details):', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // More specific error message
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        alert('Cannot connect to server. Please make sure the backend is running on port 5000.');
      } else if (error.message.includes('JSON')) {
        alert('Server error: The backend returned an invalid response. Please check the backend terminal for errors and restart the server if needed.');
      } else {
        alert(`Registration failed: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="form-section">
            <h2>1. Organization Details</h2>
            
            <div className="form-row three-col">
              <div className={`form-group ${errors.companyName ? 'has-error' : ''}`}>
                <label htmlFor="companyName" className="required">Registered company name</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName || ''}
                  onChange={handleInputChange}
                  placeholder="Enter Employer Name"
                />
                {errors.companyName && <span className="error">{errors.companyName}</span>}
              </div>

              <div className={`form-group ${errors.numberOfEmployees ? 'has-error' : ''}`}>
                <label htmlFor="numberOfEmployees" className="required">Number of employees</label>
                <select
                  id="numberOfEmployees"
                  name="numberOfEmployees"
                  value={formData.numberOfEmployees || ''}
                  onChange={handleInputChange}
                >
                  <option value="">Select number of employees</option>
                  <option value="1-10">1-10</option>
                  <option value="11-50">11-50</option>
                  <option value="51-100">51-100</option>
                  <option value="101-200">101-200</option>
                  <option value="201-500">201-500</option>
                  <option value="501-1000">501-1000</option>
                  <option value="1000+">1000+</option>
                </select>
                {errors.numberOfEmployees && <span className="error">{errors.numberOfEmployees}</span>}
              </div>

              <div className={`form-group ${errors.sector ? 'has-error' : ''}`}>
                <label htmlFor="sector" className="required">Sector</label>
                <select
                  id="sector"
                  name="sector"
                  value={formData.sector || ''}
                  onChange={handleInputChange}
                >
                  <option value="">Select sector</option>
                  {jobSectors.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
                {errors.sector && <span className="error">{errors.sector}</span>}
              </div>
            </div>

            <div className="form-row three-col">
              <div className="form-group">
                <label htmlFor="website">Website</label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website || ''}
                  onChange={handleInputChange}
                  placeholder="Enter website"
                  className="optional"
                />
              </div>

              <div className="form-group">
                <label htmlFor="brandName">Brand / Popular name</label>
                <input
                  type="text"
                  id="brandName"
                  name="brandName"
                  value={formData.brandName || ''}
                  onChange={handleInputChange}
                  placeholder="Enter alias name for Employer"
                  className="optional"
                />
              </div>

              <div className={`form-group ${errors.gstNumber ? 'has-error' : ''}`}>
                <label htmlFor="gstNumber" className="required">GST number</label>
                <input
                  type="text"
                  id="gstNumber"
                  name="gstNumber"
                  value={formData.gstNumber || ''}
                  onChange={handleInputChange}
                  placeholder="Enter 15 digit GST number"
                  className="optional"
                />
              </div>
            </div>

            <div className={`form-group ${errors.hasTransgenderPolicy ? 'has-error' : ''}`}>
              <label htmlFor="description" className="required">Short Description about Organization</label>
              <textarea
                id="description"
                name="description"
                value={formData.description || ''}
                onChange={handleInputChange}
                placeholder="Describe your organization, mission, and values (max 300 characters)"
                rows="4"
                maxLength="300"
              />
              {errors.description && <span className="error">{errors.description}</span>}
            </div>

            <div className="form-row">
              <div className={`form-group ${errors.hasTransgenderPolicy ? 'has-error' : ''}`}>
                <label className="required">Do you have Transgender Persons policy in your organization?</label>
                <div className="custom-radio-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="hasTransgenderPolicy"
                      value="yes"
                      checked={(formData.hasTransgenderPolicy || '') === 'yes'}
                      onChange={handleInputChange}
                    />
                    <span className="custom-radio-circle"></span>
                    <span className="radio-text">Yes</span>
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="hasTransgenderPolicy"
                      value="no"
                      checked={(formData.hasTransgenderPolicy || '') === 'no'}
                      onChange={handleInputChange}
                    />
                    <span className="custom-radio-circle"></span>
                    <span className="radio-text">No</span>
                  </label>
                </div>
                {errors.hasTransgenderPolicy && <span className="error">{errors.hasTransgenderPolicy}</span>}
              </div>

              <div className={`form-group ${errors.openToAccommodation ? 'has-error' : ''}`}>
                <label className="required">Are you open to reasonable accommodation?</label>
                <div className="custom-radio-group">
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="openToAccommodation"
                      value="yes"
                      checked={(formData.openToAccommodation || '') === 'yes'}
                      onChange={handleInputChange}
                    />
                    <span className="custom-radio-circle"></span>
                    <span className="radio-text">Yes</span>
                  </label>
                  <label className="custom-radio-label">
                    <input
                      type="radio"
                      name="openToAccommodation"
                      value="no"
                      checked={(formData.openToAccommodation || '') === 'no'}
                      onChange={handleInputChange}
                    />
                    <span className="custom-radio-circle"></span>
                    <span className="radio-text">No</span>
                  </label>
                </div>
                {errors.openToAccommodation && <span className="error">{errors.openToAccommodation}</span>}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="form-section">
            <h2>2. Contact Information</h2>
            
            <div className="form-row">
              <div className={`form-group ${errors.district ? 'has-error' : ''}`}>
                <label htmlFor="district" className="required">District</label>
                <select
                  id="district"
                  name="district"
                  value={formData.district || ''}
                  onChange={handleInputChange}
                >
                  <option value="">Select District</option>
                  {districts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
                {errors.district && <span className="error">{errors.district}</span>}
              </div>

              <div className={`form-group ${errors.mandal ? 'has-error' : ''}`}>
                <label htmlFor="mandal" className="required">Mandal/Taluka/Block/Sub-division</label>
                <input
                  type="text"
                  id="mandal"
                  name="mandal"
                  value={formData.mandal || ''}
                  onChange={handleInputChange}
                  placeholder="Enter Mandal/Taluka/Block/Sub-division"
                />
                {errors.mandal && <span className="error">{errors.mandal}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className={`form-group ${errors.city ? 'has-error' : ''}`}>
                <label htmlFor="city" className="required">City/Village</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city || ''}
                  onChange={handleInputChange}
                  placeholder="Enter City/Village"
                />
                {errors.city && <span className="error">{errors.city}</span>}
              </div>

              <div className={`form-group ${errors.pincode ? 'has-error' : ''}`}>
                <label htmlFor="pincode" className="required">Pincode</label>
                <input
                  type="text"
                  id="pincode"
                  name="pincode"
                  value={formData.pincode || ''}
                  onChange={handleInputChange}
                  placeholder="Enter 6-digit pincode"
                  maxLength="6"
                />
                {errors.pincode && <span className="error">{errors.pincode}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className={`form-group ${errors.contactPersonName ? 'has-error' : ''}`}>
                <label htmlFor="contactPersonName" className="required">Contact Person Name</label>
                <input
                  type="text"
                  id="contactPersonName"
                  name="contactPersonName"
                  value={formData.contactPersonName || ''}
                  onChange={handleInputChange}
                  placeholder="Enter contact person name"
                />
                {errors.contactPersonName && <span className="error">{errors.contactPersonName}</span>}
              </div>
              <div className={`form-group ${errors.department ? 'has-error' : ''}`}>
                <label htmlFor="department" className="required">Department</label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department || ''}
                  onChange={handleInputChange}
                  placeholder="Enter department"
                />
                {errors.department && <span className="error">{errors.department}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className={`form-group ${errors.designation ? 'has-error' : ''}`}>
                <label htmlFor="designation" className="required">Designation</label>
                <input
                  type="text"
                  id="designation"
                  name="designation"
                  value={formData.designation || ''}
                  onChange={handleInputChange}
                  placeholder="Enter designation"
                />
                {errors.designation && <span className="error">{errors.designation}</span>}
              </div>

              <div className={`form-group ${errors.emailId ? 'has-error' : ''}`}>
                <label htmlFor="emailId" className="required">Email ID</label>
                <input
                  type="email"
                  id="emailId"
                  name="emailId"
                  value={formData.emailId || ''}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                />
                {errors.emailId && <span className="error">{errors.emailId}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className={`form-group ${errors.mobileNumber ? 'has-error' : ''}`}>
                <label htmlFor="mobileNumber" className="required">Mobile Number</label>
                <input
                  type="tel"
                  id="mobileNumber"
                  name="mobileNumber"
                  value={formData.mobileNumber || ''}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="14"
                />
                {errors.mobileNumber && <span className="error">{errors.mobileNumber}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="alternativeMobile">Alternative Mobile Number (Optional)</label>
                <input
                  type="tel"
                  id="alternativeMobile"
                  name="alternativeMobile"
                  value={formData.alternativeMobile || ''}
                  onChange={handleInputChange}
                  placeholder="Enter alternative mobile number"
                  maxLength="14"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="form-section">
            <h2>3. Documents Upload</h2>
            <div className="upload-info">
              <p><strong>Important:</strong> You need to upload the following documents in order to process your registration. Please review the documents type and file size below, before uploading.</p>
              <p><strong>File Types:</strong> Only .jpg, .jpeg, .png, .pdf, .doc, .docx file types are allowed</p>
              <p><strong>File Size:</strong> Maximum size should be below 10 MB</p>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="registrationCertificate" className="required">1. Registration Certificate</label>
                <input
                  type="file"
                  id="registrationCertificate"
                  name="registrationCertificate"
                  onChange={handleInputChange}
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                />
                <small>Upload company registration certificate</small>
                {errors.registrationCertificate && <span className="error">{errors.registrationCertificate}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="annualReport" className="required">2. Annual Report</label>
                <input
                  type="file"
                  id="annualReport"
                  name="annualReport"
                  onChange={handleInputChange}
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                />
                <small>Upload latest annual report</small>
                {errors.annualReport && <span className="error">{errors.annualReport}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="transgenderPolicyCertificate">3. Transgender Persons Policy Certificate (Optional)</label>
                <input
                  type="file"
                  id="transgenderPolicyCertificate"
                  name="transgenderPolicyCertificate"
                  onChange={handleInputChange}
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                />
                <small>Upload Transgender Persons policy certificate (Optional)</small>
              </div>

              <div className="form-group">
                <label htmlFor="companyLogo" className="required">4. Company Logo</label>
                <input
                  type="file"
                  id="companyLogo"
                  name="companyLogo"
                  onChange={handleInputChange}
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                />
                <small>Upload company logo (Mandatory)</small>
                {errors.companyLogo && <span className="error">{errors.companyLogo}</span>}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="form-section">
            <h2>4. Login Details</h2>
            <div className={`form-group ${(submitAttempted && errors.loginEmail) ? 'has-error' : ''}`}>
              <label htmlFor="loginEmail" className="required">Enter your Mail ID</label>
              <input
                type="email"
                id="loginEmail"
                name="loginEmail"
                value={formData.loginEmail || ''}
                onChange={handleInputChange}
                placeholder="This email will be used for login and communication purposes"
              />
              {(submitAttempted && errors.loginEmail) && <span className="error">{errors.loginEmail}</span>}
            </div>

            <div className="form-row">
              <div className={`form-group ${(submitAttempted && errors.password) ? 'has-error' : ''}`}> 
                <label htmlFor="password" className="required">Create Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password || ''}
                    onChange={handleInputChange}
                    placeholder="Password should contain uppercase, lowercase and number"
                    className={(submitAttempted && errors.password) ? 'input-error' : ''}
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon visible={showPassword} />
                  </button>
                </div>
                <small style={{ color: '#888', fontSize: '0.95rem' }}>
                  Password must be at least 8 characters and contain uppercase, lowercase, and a number.
                </small>
                {(submitAttempted && errors.password) && <span className="error">{errors.password}</span>}
              </div>

              <div className={`form-group ${(submitAttempted && errors.confirmPassword) ? 'has-error' : ''}`}> 
                <label htmlFor="confirmPassword" className="required">Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword || ''}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                    className={(submitAttempted && errors.confirmPassword) ? 'input-error' : ''}
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon visible={showConfirmPassword} />
                  </button>
                </div>
                <small style={{ color: '#888', fontSize: '0.95rem' }}>
                  Must match the password entered above.
                </small>
                {(submitAttempted && errors.confirmPassword) && <span className="error">{errors.confirmPassword}</span>}
              </div>
            </div>

            <div className={`form-group ${(submitAttempted && errors.otp) ? 'has-error' : ''}`}>
              <label htmlFor="otp" className="required">OTP Authentication</label>
              <div className="otp-section">
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  value={formData.otp || ''}
                  onChange={handleInputChange}
                  placeholder="Enter 6-digit OTP"
                  maxLength="6"
                  disabled={!formData.otpSent}
                />
                <button
                  type="button"
                  className="send-otp-btn"
                  onClick={sendOTP}
                  disabled={isOtpSending}
                >
                  {isOtpSending ? 'Sending...' : formData.otpSent ? 'Resend OTP' : 'Send OTP'}
                </button>
              </div>
              <small style={{ color: formData.otpSent ? '#059669' : '#888' }}>
                {formData.otpSent 
                  ? `✓ OTP sent to ${formData.loginEmail}. Please check your email (valid for 5 minutes).` 
                  : 'Click "Send OTP" to receive verification code via email'}
              </small>
              {(submitAttempted && errors.otp) && <span className="error">{errors.otp}</span>}
            </div>
          </div>
        );

      default:
        return null;
    }
  }

  return (
    <div className="employer-registration">
      <div className="container">
        {/* centralized Back to Home button */}
        <BackHomeButton style={{ margin: '1rem 0', padding: '0.5rem 1.5rem', background: '#4169E1', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }} />
        <div className="registration-header">
          <h1>Employer Registration</h1>
          <p>Register your organization to post jobs and find talented candidates</p>
        </div>
        {/* Progress Bar */}
        <div className="progress-bar">
          <div className="progress-step active">
            <span className="step-number">1</span>
            <span className="step-label">Organization</span>
          </div>
          <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Contact</span>
          </div>
          <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Documents</span>
          </div>
          <div className={`progress-step ${currentStep >= 4 ? 'active' : ''}`}>
            <span className="step-number">4</span>
            <span className="step-label">Login</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} onKeyDown={(e) => {
          // Prevent form submission on Enter key unless on the submit button
          if (e.key === 'Enter' && e.target.type !== 'submit' && e.target.type !== 'textarea') {
            e.preventDefault();
          }
        }} className="registration-form">
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="form-navigation">
            {currentStep > 1 && (
              <button
                type="button"
                className="nav-btn prev-btn"
                onClick={(e) => prevStep(e)}
              >
                ← Back
              </button>
            )}
            
            {currentStep < 4 ? (
              <button
                type="button"
                className="nav-btn next-btn"
                onClick={(e) => nextStep(e)}
              >
                Save & Next →
              </button>
            ) : (
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Registration'}
              </button>
            )}
          </div>
        </form>

        <div className="login-link">
          <p>Already have an account? <Link to="/login">Login here</Link></p>
        </div>

        {/* Terms and Conditions Modal */}
        {showTermsModal && (
          <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
            <div className="terms-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="terms-modal-header">
                <h2>Terms and Conditions</h2>
                <button 
                  className="terms-close-btn" 
                  onClick={() => setShowTermsModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="terms-modal-body">
                <h3>Welcome to TG Jobs Telangana - Employer Registration</h3>
                <p>Please read these terms and conditions carefully before registering your organization.</p>
                
                <h4>1. Acceptance of Terms</h4>
                <p>By registering as an employer on this platform, you agree to comply with all terms and conditions set forth by the Government of Telangana.</p>
                
                <h4>2. Organization Information</h4>
                <ul>
                  <li>You must provide accurate and complete information about your organization</li>
                  <li>All submitted documents must be genuine and valid</li>
                  <li>Any false information may result in account suspension or legal action</li>
                  <li>You are responsible for maintaining the confidentiality of your login credentials</li>
                </ul>
                
                <h4>3. Privacy and Data Protection</h4>
                <ul>
                  <li>Your information will be used solely for employment facilitation purposes</li>
                  <li>Data will be handled in accordance with applicable privacy laws</li>
                  <li>We may share your information with relevant government departments</li>
                  <li>You consent to the collection and processing of your data as per our privacy policy</li>
                </ul>
                
                <h4>4. Account Approval</h4>
                <ul>
                  <li>Registration is subject to administrative approval</li>
                  <li>The approval process may take 3-5 business days</li>
                  <li>Authorities reserve the right to reject any application without providing reasons</li>
                  <li>You will be notified via email/SMS regarding your application status</li>
                </ul>
                
                <h4>5. Employment Opportunities</h4>
                <ul>
                  <li>Job postings must be genuine and comply with labor laws</li>
                  <li>Discrimination based on gender identity is strictly prohibited</li>
                  <li>Positions must offer equal opportunities to transgender candidates</li>
                  <li>False job postings may result in account termination and legal action</li>
                </ul>
                
                <h4>6. Prohibited Activities</h4>
                <ul>
                  <li>Posting discriminatory or offensive content</li>
                  <li>Misusing candidate information for purposes other than recruitment</li>
                  <li>Attempting to gain unauthorized access to the system</li>
                  <li>Sharing your login credentials with unauthorized persons</li>
                </ul>
                
                <h4>7. Government Services</h4>
                <p>This platform is a government initiative providing free employment facilitation services to promote inclusive employment for the transgender community in Telangana.</p>
                
                <h4>8. Contact Information</h4>
                <p><strong>TG Helpline:</strong> 155326</p>
                <p><strong>Email:</strong> cdwtghyd@gmail.com</p>
                
                <h4>9. Modifications</h4>
                <p>The Government of Telangana reserves the right to modify these terms at any time. Continued use of the platform constitutes acceptance of modified terms.</p>
              </div>
              <div className="terms-modal-footer">
                <button 
                  className="terms-decline-btn" 
                  onClick={() => setShowTermsModal(false)}
                >
                  Decline
                </button>
                <button 
                  className="terms-accept-btn" 
                  onClick={handleAcceptTerms}
                >
                  I Accept Terms & Conditions
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmployerRegistration;